import React from "react";
import * as styles from "web/react/components/checkout/v2/checkout-modal-layout.css";
import { Button } from "web/react/emo/button";
import { Heading } from "web/react/emo/heading";
import { Modal } from "web/react/emo/modal";
import { Stack } from "web/react/emo/shared/components/stack";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import { gettext } from "web/script/modules/django-i18n";

interface PrimaryButton {
    title?: string;
    onClick?: () => void;
}

interface SecondaryButton {
    title: string;
    onClick: () => void;
}

export interface CheckoutErrorModalProps {
    isOpen: boolean;
    onClose: () => void;
    heading?: React.ReactNode;
    content?: React.ReactNode;
    primaryButton?: PrimaryButton;
    secondaryButton?: SecondaryButton;
    dataTestId?: string;
}

export function CheckoutErrorModal({
    isOpen,
    onClose,
    heading = gettext("error.general_modal.title"),
    content = gettext("error.general_modal.description"),
    primaryButton,
    secondaryButton,
    dataTestId,
}: CheckoutErrorModalProps): React.ReactElement {
    const resolvedPrimaryTitle = primaryButton?.title ?? gettext("general.close");
    const primaryButtonOnClick = primaryButton?.onClick ?? onClose;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            aria={{
                labelledby: "checkout-error-modal-title",
                describedby: "checkout-error-modal-desc",
            }}
            role="alertdialog"
        >
            <View className={styles.container} dataTestId={dataTestId}>
                <Heading id="checkout-error-modal-title" as="h2" textStyle="body-bold">
                    {heading}
                </Heading>
                <View marginBottom="sm" marginTop="xxs" className={styles.divider} />
                <View className={styles.contentContainer}>
                    <Text id="checkout-error-modal-desc" as="p" textStyle="body-s">
                        {content}
                    </Text>
                    {secondaryButton ? (
                        <Stack direction="row" spacing="xxs">
                            <Button
                                width="full"
                                title={secondaryButton.title}
                                variant="secondary"
                                onClick={secondaryButton.onClick}
                            />
                            <Button
                                width="full"
                                title={resolvedPrimaryTitle}
                                onClick={primaryButtonOnClick}
                            />
                        </Stack>
                    ) : (
                        <Button
                            width="full"
                            title={resolvedPrimaryTitle}
                            onClick={primaryButtonOnClick}
                        />
                    )}
                </View>
            </View>
        </Modal>
    );
}
